import { useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Star } from "lucide-react";

const EMPTY = { name: "", location: "", country: "", stars: 1, cuisine_type: "" };

const AddRestaurantDialog = ({ open, onOpenChange }) => {
  const { t } = useTranslation();
  const [newRestaurant, setNewRestaurant] = useState(EMPTY);

  const handleSubmit = async () => {
    if (!newRestaurant.name || !newRestaurant.location || !newRestaurant.country) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      await axios.post(`${API}/restaurants`, newRestaurant, { withCredentials: true });
      toast.success(
        <div>
          <p className="font-semibold">Restaurant-Anfrage eingereicht!</p>
          <p className="text-sm">Sie werden per E-Mail benachrichtigt, sobald &quot;{newRestaurant.name}&quot; hinzugefügt wurde.</p>
        </div>,
        { duration: 6000 }
      );
      onOpenChange(false);
      setNewRestaurant(EMPTY);
    } catch (error) {
      toast.error("Failed to submit restaurant request");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border" data-testid="add-restaurant-dialog">
        <DialogHeader>
          <DialogTitle>{t('restaurants.addUnlisted')}</DialogTitle>
        </DialogHeader>
        <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg mb-2">
          <p className="text-sm text-primary">
            Ihr Restaurant-Vorschlag wird von unserem Team geprüft und schnellstmöglich hinzugefügt.
            Sie werden per E-Mail benachrichtigt.
          </p>
        </div>
        <div className="space-y-4 pt-2">
          <div>
            <Label className="text-sm text-muted-foreground mb-2 block">{t('restaurants.restaurantName')} *</Label>
            <Input
              placeholder="e.g., The French Laundry"
              value={newRestaurant.name}
              onChange={(e) => setNewRestaurant({ ...newRestaurant, name: e.target.value })}
              className="bg-transparent border-border focus:border-primary"
              data-testid="new-restaurant-name"
            />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-2 block">{t('restaurants.location')} *</Label>
            <Input
              placeholder="e.g., Yountville, CA"
              value={newRestaurant.location}
              onChange={(e) => setNewRestaurant({ ...newRestaurant, location: e.target.value })}
              className="bg-transparent border-border focus:border-primary"
              data-testid="new-restaurant-location"
            />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-2 block">{t('restaurants.country')} *</Label>
            <Input
              placeholder="e.g., USA"
              value={newRestaurant.country}
              onChange={(e) => setNewRestaurant({ ...newRestaurant, country: e.target.value })}
              className="bg-transparent border-border focus:border-primary"
              data-testid="new-restaurant-country"
            />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-2 block">{t('restaurants.michelinStars')} *</Label>
            <div className="flex gap-2">
              {[1, 2, 3].map((stars) => (
                <Button
                  key={`star-btn-${stars}`}
                  variant={newRestaurant.stars === stars ? "default" : "outline"}
                  onClick={() => setNewRestaurant({ ...newRestaurant, stars })}
                  className={newRestaurant.stars === stars ? "bg-primary text-primary-foreground" : "border-border"}
                  data-testid={`star-select-${stars}`}
                >
                  {[...Array(stars)].map((_, i) => (
                    <Star key={`new-star-${stars}-${i}`} className="w-4 h-4" />
                  ))}
                </Button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-2 block">{t('restaurants.cuisineType')}</Label>
            <Input
              placeholder="e.g., French, Nordic, Plant-based"
              value={newRestaurant.cuisine_type}
              onChange={(e) => setNewRestaurant({ ...newRestaurant, cuisine_type: e.target.value })}
              className="bg-transparent border-border focus:border-primary"
              data-testid="new-restaurant-cuisine"
            />
          </div>
          <Button
            onClick={handleSubmit}
            className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="submit-new-restaurant"
          >
            Restaurant-Anfrage senden
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddRestaurantDialog;
